import { useState, useCallback, useRef, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/hooks/use-auth";
import { ArrowRight, Upload, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle, X } from "lucide-react";

type Source = "generic" | "shopify" | "woocommerce";

interface ImportError {
  readonly row: number;
  readonly error: string;
}

interface ImportResultData {
  readonly imported: number;
  readonly skipped: number;
  readonly errors: readonly ImportError[];
}

const SOURCE_OPTIONS: readonly { value: Source; label: string; desc: string; badge?: string }[] = [
  { value: "generic", label: "גנרי", desc: "CSV עם עמודות name, price, description, sku, stock" },
  { value: "shopify", label: "Shopify", desc: "קובץ יצוא מ-Shopify (Products CSV)", badge: "נפוץ" },
  { value: "woocommerce", label: "WooCommerce", desc: "קובץ יצוא מ-WooCommerce (Products CSV)" },
];

export function ProductImportPage(): ReactNode {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);

  const [source, setSource] = useState<Source>("generic");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<ImportResultData | null>(null);
  const [error, setError] = useState("");

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) {
      setFile(f);
      setResult(null);
      setError("");
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f && f.name.endsWith(".csv")) {
      setFile(f);
      setResult(null);
      setError("");
    }
  }, []);

  const handleImport = useCallback(async () => {
    if (!file) return;
    setUploading(true);
    setError("");
    setResult(null);
    setProgress(0);

    // Simulate progress ticks while waiting
    const interval = setInterval(() => {
      setProgress((p) => Math.min(p + 8, 85));
    }, 300);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("source", source);

      const res = await fetch("/api/products/import", {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: formData,
      });

      clearInterval(interval);
      setProgress(100);

      const json = await res.json() as { success: boolean; data?: ImportResultData; error?: string };
      if (!json.success) {
        setError(json.error ?? "שגיאה בייבוא");
        return;
      }
      if (json.data) {
        setResult(json.data);
      }
    } catch {
      clearInterval(interval);
      setError("שגיאה בחיבור לשרת");
    } finally {
      setUploading(false);
    }
  }, [file, source, accessToken]);

  return (
    <div className="p-4 md:p-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-2 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate("/products")} className="min-h-[38px]">
          <ArrowRight className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">ייבוא מוצרים</h1>
          <p className="text-sm text-muted-foreground mt-0.5">ייבא מוצרים מקובץ CSV</p>
        </div>
      </div>

      {/* Source selection */}
      <Card className="mb-4 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">מקור הקובץ</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {SOURCE_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={`flex items-start gap-3 p-3.5 rounded-lg border cursor-pointer transition-all ${
                source === opt.value
                  ? "border-primary bg-primary/5 shadow-sm"
                  : "hover:bg-muted/40 hover:border-muted-foreground/30"
              }`}
            >
              <input
                type="radio"
                name="source"
                value={opt.value}
                checked={source === opt.value}
                onChange={() => setSource(opt.value)}
                className="mt-0.5"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-sm">{opt.label}</p>
                  {opt.badge && (
                    <span className="bg-primary/15 text-primary text-xs px-1.5 py-0.5 rounded-full font-medium">
                      {opt.badge}
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{opt.desc}</p>
              </div>
            </label>
          ))}
        </CardContent>
      </Card>

      {/* File upload */}
      <Card className="mb-4 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">קובץ CSV</CardTitle>
        </CardHeader>
        <CardContent>
          <div
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
              file ? "border-primary/40 bg-primary/5" : "hover:bg-muted/30 hover:border-muted-foreground/40"
            }`}
            onClick={() => fileRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
          >
            {file ? (
              <div className="space-y-2">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <FileSpreadsheet className="h-6 w-6 text-primary" />
                </div>
                <p className="font-medium text-sm">{file.name}</p>
                <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(0)} KB</p>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setFile(null); }}
                  className="text-xs text-destructive hover:underline flex items-center gap-1 mx-auto"
                >
                  <X className="h-3 w-3" />
                  הסר
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mx-auto">
                  <Upload className="h-6 w-6 text-muted-foreground" />
                </div>
                <p className="font-medium text-sm">גרור קובץ לכאן או לחץ לבחירה</p>
                <p className="text-xs text-muted-foreground">קבצי CSV בלבד</p>
              </div>
            )}
            <input
              ref={fileRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 bg-destructive/10 text-destructive text-sm p-3 rounded-lg mb-4 border border-destructive/20">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {/* Progress bar during upload */}
      {uploading && (
        <div className="mb-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">מייבא מוצרים...</span>
            <span className="font-medium">{progress}%</span>
          </div>
          <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Import button */}
      {!result && (
        <Button
          onClick={handleImport}
          disabled={!file || uploading}
          className="w-full min-h-[44px]"
        >
          {uploading ? (
            <Loader2 className="h-4 w-4 animate-spin me-1.5" />
          ) : (
            <Upload className="h-4 w-4 me-1.5" />
          )}
          {uploading ? "מייבא..." : "ייבוא מוצרים"}
        </Button>
      )}

      {/* Results */}
      {result && (
        <Card className="shadow-sm">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 className="h-5 w-5" />
              <span className="font-semibold">ייבוא הושלם</span>
            </div>

            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/20">
                <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">{result.imported}</p>
                <p className="text-xs text-muted-foreground mt-1">יובאו</p>
              </div>
              <div className="p-4 bg-yellow-500/10 rounded-xl border border-yellow-500/20">
                <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">{result.skipped}</p>
                <p className="text-xs text-muted-foreground mt-1">דולגו</p>
              </div>
              <div className="p-4 bg-destructive/10 rounded-xl border border-destructive/20">
                <p className="text-3xl font-bold text-destructive">{result.errors.length}</p>
                <p className="text-xs text-muted-foreground mt-1">שגיאות</p>
              </div>
            </div>

            {result.errors.length > 0 && (
              <details className="text-sm">
                <summary className="cursor-pointer flex items-center gap-1.5 text-yellow-600 dark:text-yellow-400 font-medium">
                  <AlertTriangle className="h-4 w-4" />
                  פרטי שגיאות ({result.errors.length})
                </summary>
                <div className="mt-3 space-y-1.5 max-h-48 overflow-y-auto rounded-lg border p-3 bg-muted/30">
                  {result.errors.map((err, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs">
                      <span className="bg-destructive/15 text-destructive rounded px-1.5 py-0.5 font-mono shrink-0">
                        שורה {err.row}
                      </span>
                      <span className="text-muted-foreground">{err.error}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}

            <div className="flex gap-2 pt-1">
              <Button
                className="flex-1 min-h-[44px]"
                onClick={() => navigate("/products")}
              >
                צפה במוצרים
              </Button>
              <Button
                variant="outline"
                className="min-h-[44px]"
                onClick={() => { setResult(null); setFile(null); setProgress(0); }}
              >
                ייבוא נוסף
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
